import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API_BASE_URL } from "@/lib/config";
import {
  applySharedRefs,
  buildPayloadFromForm,
  defaultLinerWebhookMockFormState,
  hydrateFormFromPayload,
  type LinerWebhookMockFormState,
} from "@/lib/linerWebhookMockForm";
import { api } from "@/lib/api";
import { API_PATHS } from "@/lib/api-paths";
import { cn } from "@/lib/utils";
import {
  LINER_WEBHOOK_PRESETS,
  getLinerWebhookPreset,
} from "@/types/linerWebhookPresets";
import { ArrowLeft, RefreshCw, Send } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

const textareaClass = cn(
  "border-input placeholder:text-muted-foreground flex w-full min-w-0 rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] ring-ring/10 dark:ring-ring/20 dark:outline-ring/40 outline-ring/50 focus-visible:ring-4 focus-visible:outline-1 md:text-sm",
  "min-h-[140px] font-mono text-xs leading-relaxed"
);

export function LinerWebhookMockPage() {
  const [form, setForm] = useState<LinerWebhookMockFormState>(() =>
    defaultLinerWebhookMockFormState()
  );
  const [presetId, setPresetId] = useState<string>("maersk_booking_v2_initial_confirmed");
  const [linerApiKey, setLinerApiKey] = useState("");
  const [linerOutboundWebhookKey, setLinerOutboundWebhookKey] = useState("");
  const [linerSkipOutboundClientHeader, setLinerSkipOutboundClientHeader] =
    useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resp, setResp] = useState<{
    status: number;
    data: unknown;
    ok: boolean;
  } | null>(null);

  const preview = useMemo(() => {
    const r = buildPayloadFromForm(form);
    if (!r.ok) return { text: "", error: r.error };
    return { text: JSON.stringify(r.payload, null, 2), error: null as string | null };
  }, [form]);

  useEffect(() => {
    setBuildError(preview.error);
  }, [preview.error]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await api.get<{
        inboundApiKey?: string;
        outboundWebhookApiKey?: string;
      }>(API_PATHS.LINER_WEBHOOKS_DEFAULTS);
      if (cancelled || !res.ok || !res.data) return;
      const d = res.data;
      if (typeof d.inboundApiKey === "string" && d.inboundApiKey) {
        setLinerApiKey(d.inboundApiKey);
      }
      if (typeof d.outboundWebhookApiKey === "string") {
        setLinerOutboundWebhookKey(d.outboundWebhookApiKey);
        setLinerSkipOutboundClientHeader(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadPreset = useCallback((id: string) => {
    const p = getLinerWebhookPreset(id);
    if (!p) return;
    const raw = structuredClone(p.payload) as Record<string, unknown>;
    setForm(hydrateFormFromPayload(raw));
    setPresetId(id);
    setBuildError(null);
  }, []);

  const regenerateIds = useCallback(() => {
    setForm((prev) => {
      const n = defaultLinerWebhookMockFormState();
      return {
        ...prev,
        cloudId: n.cloudId,
        cloudTime: n.cloudTime,
      };
    });
  }, []);

  const syncRefsFromCloud = useCallback(() => {
    setForm((s) => applySharedRefs(s, s.cloudLsaBookingRef, s.cloudLinerCode));
  }, []);

  const handleSend = useCallback(async () => {
    const built = buildPayloadFromForm(form);
    if (!built.ok) {
      setBuildError(built.error);
      setResp({ status: 0, data: built.error, ok: false });
      return;
    }
    if (!linerApiKey.trim()) {
      setResp({
        status: 0,
        data: "X-API-Key is required for Liner webhook send-mock",
        ok: false,
      });
      return;
    }
    setLoading(true);
    setResp(null);
    const res = await api.post(API_PATHS.LINER_WEBHOOKS_SEND_MOCK, {
      body: built.payload,
      apiKey: linerApiKey.trim(),
      headers: linerSkipOutboundClientHeader
        ? undefined
        : { "X-Liner-Webhook-Client-Key": linerOutboundWebhookKey },
    });
    setResp(res);
    setLoading(false);
  }, [form, linerApiKey, linerOutboundWebhookKey, linerSkipOutboundClientHeader]);

  const update = <K extends keyof LinerWebhookMockFormState>(
    key: K,
    value: LinerWebhookMockFormState[K]
  ) => {
    setForm((s) => ({ ...s, [key]: value }));
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 pb-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <a
            href="#/"
            className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            Back to Simulation UI
          </a>
          <h2 className="font-heading text-2xl font-bold tracking-tight">
            Liner webhook — send-mock builder
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            CloudEvents 1.0 JSON using Maersk / DCSA Booking 2.0:{" "}
            <code className="text-xs">type</code> <code className="text-xs">org.dcsa.booking.v2</code>,{" "}
            <code className="text-xs">source</code> <code className="text-xs">/maersk/ocean/booking/v2</code>, and{" "}
            <code className="text-xs">data</code>. POST targets{" "}
            <code className="text-xs">
              {API_BASE_URL}
              {API_PATHS.LINER_WEBHOOKS_SEND_MOCK}
            </code>
            ; the server forwards JSON to <code className="text-xs">LINER_WEBHOOK_URL</code>.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={regenerateIds}>
            <RefreshCw className="mr-1.5 size-4" />
            New CloudEvents id / time
          </Button>
          <Button type="button" size="sm" onClick={() => void handleSend()} disabled={loading}>
            <Send className="mr-1.5 size-4" />
            {loading ? "Sending…" : "Send mock webhook"}
          </Button>
        </div>
      </div>

      <Card className="border-white/10 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Auth &amp; presets</CardTitle>
          <CardDescription>
            Same headers as the Liner tab: mock <code className="text-xs">X-API-Key</code> and optional
            outbound <code className="text-xs">X-Liner-Webhook-Client-Key</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>X-API-Key (inbound to this mock)</Label>
              <Input
                className="mt-1 font-mono"
                value={linerApiKey}
                onChange={(e) => setLinerApiKey(e.target.value)}
                placeholder="Saved inbound API key"
              />
            </div>
            <div>
              <Label>Outbound X-API-Key (to LINER_WEBHOOK_URL)</Label>
              <Input
                className="mt-1 font-mono"
                value={linerOutboundWebhookKey}
                onChange={(e) => setLinerOutboundWebhookKey(e.target.value)}
                disabled={linerSkipOutboundClientHeader}
                placeholder="Saved outbound webhook API key"
              />
              <label className="mt-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded"
                  checked={linerSkipOutboundClientHeader}
                  onChange={(e) => setLinerSkipOutboundClientHeader(e.target.checked)}
                />
                Omit <code className="text-xs">X-Liner-Webhook-Client-Key</code> on outbound POST
              </label>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px] flex-1">
              <Label>Preset (CloudEvents templates)</Label>
              <Select value={presetId} onValueChange={(v) => loadPreset(v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LINER_WEBHOOK_PRESETS.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="button" variant="secondary" onClick={() => loadPreset(presetId)}>
              Reload preset
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 shadow-sm">
        <CardHeader className="pb-2">
              <CardTitle className="text-base">CloudEvents — envelope</CardTitle>
          <CardDescription>
            <code className="text-xs">specversion</code>, <code className="text-xs">id</code>,{" "}
            <code className="text-xs">source</code>, <code className="text-xs">type</code>,{" "}
            <code className="text-xs">time</code>, <code className="text-xs">datacontenttype</code>, plus root{" "}
            <code className="text-xs">lsaBookingRef</code> / <code className="text-xs">linerCode</code> /{" "}
            <code className="text-xs">bookingStatus</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={syncRefsFromCloud}>
              Sync root LSA ref &amp; liner code → data.* refs
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field
              label="specversion"
              value={form.cloudSpecVersion}
              onChange={(v) => update("cloudSpecVersion", v)}
            />
            <Field label="id" value={form.cloudId} onChange={(v) => update("cloudId", v)} />
            <Field
              label="source"
              value={form.cloudSource}
              onChange={(v) => update("cloudSource", v)}
            />
            <div className="sm:col-span-2">
              <Label>type</Label>
              <Input
                className="mt-1 font-mono text-xs"
                value={form.cloudType}
                onChange={(e) => update("cloudType", e.target.value)}
                list="liner-webhook-cloud-event-types"
                placeholder="org.dcsa.booking.v2"
              />
              <datalist id="liner-webhook-cloud-event-types">
                <option value="org.dcsa.booking.v2" />
              </datalist>
            </div>
            <Field label="time (ISO)" value={form.cloudTime} onChange={(v) => update("cloudTime", v)} />
            <Field
              label="datacontenttype"
              value={form.cloudDataContentType}
              onChange={(v) => update("cloudDataContentType", v)}
            />
            <Field
              label="lsaBookingRef (root)"
              value={form.cloudLsaBookingRef}
              onChange={(v) => update("cloudLsaBookingRef", v)}
            />
            <Field
              label="linerCode (root)"
              value={form.cloudLinerCode}
              onChange={(v) => update("cloudLinerCode", v)}
            />
            <Field
              label="bookingStatus (root)"
              value={form.cloudBookingStatus}
              onChange={(v) => update("cloudBookingStatus", v)}
            />
            <Field
              label="subject (optional extension)"
              value={form.cloudSubject}
              onChange={(v) => update("cloudSubject", v)}
            />
            <Field
              label="subscriptionReference (Maersk / Booking 2.0)"
              value={form.cloudSubscriptionReference}
              onChange={(v) => update("cloudSubscriptionReference", v)}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">CloudEvents — data (BookingNotificationData)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field
              label="data.lsaBookingRef"
              value={form.cloudDataLsaBookingRef}
              onChange={(v) => update("cloudDataLsaBookingRef", v)}
            />
            <Field
              label="data.linerCode"
              value={form.cloudDataLinerCode}
              onChange={(v) => update("cloudDataLinerCode", v)}
            />
            <Field
              label="data.bookingStatus"
              value={form.cloudDataBookingStatus}
              onChange={(v) => update("cloudDataBookingStatus", v)}
            />
            <Field
              label="data.amendedBookingStatus"
              value={form.cloudDataAmendedBookingStatus}
              onChange={(v) => update("cloudDataAmendedBookingStatus", v)}
            />
            <Field
              label="data.bookingCancellationStatus"
              value={form.cloudDataBookingCancellationStatus}
              onChange={(v) => update("cloudDataBookingCancellationStatus", v)}
            />
            <Field
              label="data.carrierBookingRequestReference"
              value={form.cloudDataCarrierBookingRequestReference}
              onChange={(v) => update("cloudDataCarrierBookingRequestReference", v)}
            />
            <Field
              label="data.carrierBookingReference"
              value={form.cloudDataCarrierBookingReference}
              onChange={(v) => update("cloudDataCarrierBookingReference", v)}
            />
            <Field
              label="data.transportDocumentReference"
              value={form.cloudDataTransportDocumentReference}
              onChange={(v) => update("cloudDataTransportDocumentReference", v)}
            />
            <Field
              label="data.cancelStatus"
              value={form.cloudDataCancelStatus}
              onChange={(v) => update("cloudDataCancelStatus", v)}
            />
            <Field
              label="data.cancelReason"
              value={form.cloudDataCancelReason}
              onChange={(v) => update("cloudDataCancelReason", v)}
            />
            <Field
              label="data.cancelAction"
              value={form.cloudDataCancelAction}
              onChange={(v) => update("cloudDataCancelAction", v)}
            />
            <Field
              label="data.amendmentStatus (Maersk; enter (null) for JSON null)"
              value={form.cloudDataAmendmentStatus}
              onChange={(v) => update("cloudDataAmendmentStatus", v)}
            />
            <Field
              label="data.reason"
              value={form.cloudDataReason}
              onChange={(v) => update("cloudDataReason", v)}
            />
            <Field
              label="data.updatedDateTime"
              value={form.cloudDataUpdatedDateTime}
              onChange={(v) => update("cloudDataUpdatedDateTime", v)}
            />
          </div>
          <JsonArea
            label="data.metadata (JSON object, Maersk)"
            value={form.cloudDataMetadataJson}
            onChange={(v) => update("cloudDataMetadataJson", v)}
          />
          <JsonArea
            label="data.feedbacks (JSON array)"
            value={form.cloudDataFeedbacksJson}
            onChange={(v) => update("cloudDataFeedbacksJson", v)}
          />
          <JsonArea
            label="data.booking (DCSABookingNotification JSON)"
            value={form.cloudDataBookingJson}
            onChange={(v) => update("cloudDataBookingJson", v)}
          />
          <JsonArea
            label="data.amendedBooking (JSON)"
            value={form.cloudDataAmendedBookingJson}
            onChange={(v) => update("cloudDataAmendedBookingJson", v)}
          />
          <JsonArea
            label="data.shipmentCutOffTimes (JSON array)"
            value={form.cloudDataShipmentCutOffTimesJson}
            onChange={(v) => update("cloudDataShipmentCutOffTimesJson", v)}
          />
        </CardContent>
      </Card>

      <Card className="border-white/10 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Request preview</CardTitle>
          <CardDescription>Built from the form; invalid JSON in any JSON area blocks send.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {buildError && (
            <div
              className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              role="alert"
            >
              {buildError}
            </div>
          )}
          <textarea
            readOnly
            className={cn(textareaClass, "min-h-[240px] opacity-90")}
            value={preview.text}
            aria-label="JSON preview"
          />
          {resp && (
            <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
              <p className="font-medium">
                Response <span className="text-muted-foreground">HTTP {resp.status}</span>
              </p>
              <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-all font-mono text-xs">
                {typeof resp.data === "string"
                  ? resp.data
                  : JSON.stringify(resp.data, null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <Label className="text-muted-foreground">{props.label}</Label>
      <Input className="mt-1 font-mono text-xs" value={props.value} onChange={(e) => props.onChange(e.target.value)} />
    </div>
  );
}

function JsonArea(props: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <Label className="text-muted-foreground">{props.label}</Label>
      <textarea
        className={cn(textareaClass, "mt-1")}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        spellCheck={false}
      />
    </div>
  );
}
